import { useState } from "react";
import Image from "next/image";
import { sanitizeHTML, generateAnchorId } from "@/lib/utils";

interface VideoDiariesData {
  heading?: {
    title?: string;
  };
  videos?: {
    videos_id: {
      embed_url: string;
      embed_title: string;
      description?: string;
    };
  }[];
}

export const fields = [
  "heading.title",
  "videos.videos_id.embed_url",
  "videos.videos_id.embed_title",
  "videos.videos_id.description",
] as const;

export default function VideoDiaries({ data }: { data?: VideoDiariesData }) {
  const [playingIndex, setPlayingIndex] = useState<number | null>(null);

  const videos = data?.videos || [];
  const title = data?.heading?.title || 'Latest Patient Reviews';
  const anchorId = generateAnchorId(title);

  const getYouTubeVideoId = (url: string): string | null => {
    try {
      const trimmed = url.trim();
      // Already embed URL: https://www.youtube.com/embed/<id>
      const embedMatch = trimmed.match(/\/embed\/([^?&#/]+)/);
      if (embedMatch) return embedMatch[1];

      // Watch URL: https://www.youtube.com/watch?v=<id>
      const urlObj = new URL(trimmed);
      const vParam = urlObj.searchParams.get("v");
      if (vParam) return vParam;

      // Shorts URL: https://www.youtube.com/shorts/<id>
      const shortsMatch = trimmed.match(/youtube\.com\/shorts\/([^?&#/]+)/);
      if (shortsMatch) return shortsMatch[1];

      // Short URL: https://youtu.be/<id>
      if (urlObj.hostname.includes("youtu.be")) {
        const path = urlObj.pathname.replace(/^\//, "");
        if (path) return path.split("/")[0];
      }
    } catch (e) {
      // no-op
    }
    // Bare video ID case (common: 11 chars)
    const maybeId = url.trim();
    if (/^[a-zA-Z0-9_-]{11}$/.test(maybeId)) return maybeId;
    return null;
  };

  const getYouTubeThumbnailUrl = (
    videoId: string,
    quality: "default" | "hq" | "maxres" = "maxres"
  ): string => {
    if (quality === "default") return `https://img.youtube.com/vi/${videoId}/0.jpg`;
    if (quality === "hq") return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
    return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
  };

  // no toYouTubeEmbedUrl helper needed; we build from id directly

  return (
		<>
			<section
				id={anchorId}
				className='VideoDiaries text-base md:my-24 my-16'
			>
				<div className='container'>
					<h2
						className='font-freightBig text-4xl leading-tight md:leading-normal md:text-5xl text-secondary-dark mb-8 text-left'
						dangerouslySetInnerHTML={sanitizeHTML(title)}
					/>

					{videos.length === 0 ? (
						<div className='text-center py-12'>
							<p className='text-gray-500 text-lg'>
								No videos available at the moment.
							</p>
						</div>
					) : (
						<div className='grid grid-cols-1 lg:grid-cols-3 gap-8'>
							{videos.map((item, index) => {
								const embedUrl = (item?.videos_id?.embed_url || '').trim()
								const title = item?.videos_id?.embed_title || ''
								const description = item?.videos_id?.description || ''
								const youTubeId = getYouTubeVideoId(embedUrl)
								const isPlaying = playingIndex === index
								const iframeSrc = youTubeId
									? `https://www.youtube.com/embed/${youTubeId}?autoplay=1&rel=0&modestbranding=1`
									: embedUrl

								return (
									<div
										key={`${embedUrl}-${index}`}
										className='bg-white rounded-lg shadow-lg overflow-hidden relative md:pb-[90px]'
									>
										<div className='aspect-video relative'>
											{isPlaying ? (
												<iframe
													src={iframeSrc}
													title={title}
													className='w-full h-full absolute inset-0'
													frameBorder='0'
													allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share'
													allowFullScreen
												></iframe>
											) : (
												<div
													className='relative w-full h-full cursor-pointer group'
													onClick={() => setPlayingIndex(index)}
												>
													{youTubeId ? (
														<Image
															src={getYouTubeThumbnailUrl(youTubeId)}
															alt={title}
															fill
															className='object-cover transition-transform duration-300'
															unoptimized
															priority={false}
														/>
													) : (
														<div className='w-full h-full bg-gray-200 flex items-center justify-center'>
															<span className='text-gray-500'>
																Video Preview
															</span>
														</div>
													)}
													<div className='absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/30 transition-colors'>
														<div className='group-hover:scale-110 transition-all duration-300'>
															<svg
																width='68'
																height='48'
																viewBox='0 0 68 48'
																className='drop-shadow-lg md:w-[68px] w-[40px]'
															>
																<path
																	className='fill-red-600 group-hover:fill-red-700'
																	d='M66.52,7.74c-0.78-2.93-2.49-5.41-5.42-6.19C55.79,.13,34,0,34,0S12.21,.13,6.9,1.55 C3.97,2.33,2.27,4.81,1.48,7.74C0.06,13.05,0,24,0,24s0.06,10.95,1.48,16.26c0.78,2.93,2.49,5.41,5.42,6.19 C12.21,47.87,34,48,34,48s21.79-0.13,27.1-1.55c2.93-0.78,4.64-3.26,5.42-6.19C67.94,34.95,68,24,68,24S67.94,13.05,66.52,7.74z'
																></path>
																<path
																	className='fill-white'
																	d='M 45,24 27,14 27,34'
																></path>
															</svg>
														</div>
													</div>
												</div>
											)}
										</div>
										<div className='p-4'>
											{description ? (
												<div
													className='mt-2 text-gray-600 leading-relaxed grid gap-4 md:[&_.btn]:absolute md:[&_.btn]:bottom-[25px]'
													dangerouslySetInnerHTML={sanitizeHTML(
														description || ''
													)}
												/>
											) : null}
										</div>
									</div>
								)
							})}
						</div>
					)}
				</div>
			</section>
		</>
	)
}
